import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export interface FilterState {
  startDate: string;
  endDate: string;
  gridIds: string[];
}

export const useUrlState = () => {
  const [searchParams] = useSearchParams();

  // Initialise from URL on mount — functional initialiser runs once, no reactive loop.
  // No ?start or ?end means no dates: a delivery day is chosen from the availability list, never
  // assumed here.
  const [filterState, setFilterState] = useState<FilterState>(() => ({
    startDate: searchParams.get('start') ?? '',
    endDate: searchParams.get('end') ?? '',
    gridIds: [],
  }));

  // One-time cleanup: remove legacy 'grids' URL param if present
  useEffect(() => {
    if (searchParams.get('grids')) {
      const newParams = new URLSearchParams(window.location.search);
      newParams.delete('grids');
      window.history.replaceState(window.history.state, '', `${window.location.pathname}?${newParams}`);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync dates → URL whenever they change. Only start and end are touched, so any other query
  // parameter on the page survives; with no dates and none in the URL nothing is written.
  // Use window.history.replaceState directly to avoid react-router clearing
  // location.state (which would break pages that read edit state from location).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (filterState.startDate) params.set('start', filterState.startDate);
    else params.delete('start');
    if (filterState.endDate) params.set('end', filterState.endDate);
    else params.delete('end');
    const qs = params.toString();
    if (qs === window.location.search.replace(/^\?/, '')) return; // nothing to change
    window.history.replaceState(
      window.history.state,
      '',
      window.location.pathname + (qs ? `?${qs}` : '')
    );
  }, [filterState.startDate, filterState.endDate]);

  // All mutations use functional form — no stale closure issues regardless of
  // how many times updateFilters is called synchronously in a row.
  const updateFilters = useCallback((updates: Partial<FilterState>) => {
    setFilterState(prev => ({ ...prev, ...updates }));
  }, []);

  const addGridId = useCallback((gridId: string) => {
    setFilterState(prev => {
      if (prev.gridIds.includes(gridId)) return prev;
      return { ...prev, gridIds: [...prev.gridIds, gridId] };
    });
  }, []);

  const removeGridId = useCallback((gridId: string) => {
    setFilterState(prev => ({
      ...prev,
      gridIds: prev.gridIds.filter(id => id !== gridId),
    }));
  }, []);

  const setStartDate = useCallback((startDate: string) => {
    setFilterState(prev => ({ ...prev, startDate }));
  }, []);

  const setEndDate = useCallback((endDate: string) => {
    setFilterState(prev => ({ ...prev, endDate }));
  }, []);

  const clearGridSelection = useCallback(() => {
    setFilterState(prev => ({ ...prev, gridIds: [] }));
  }, []);

  return {
    filterState,
    updateFilters,
    addGridId,
    removeGridId,
    setStartDate,
    setEndDate,
    clearGridSelection,
  };
};
